import type { ListT } from "tweenly"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { QueryOptionsT } from "../../components/common/DataGrid"
import { Status } from "../../constants/status"
import { get, put } from "../../helpers/ajax.helpers"
import { useToast } from "../../hooks/useToast"
import { AppThunkT } from "../store"
import { getCreatedAt, UserT } from "./user.slice"

interface UsersStateT {
    data: UserT[]
    total: number
    status?: string
}

const initialState: UsersStateT = {
    data: [],
    total: 0,
}

export const usersSlice = createSlice({
    name: "users",
    initialState,
    reducers: {
        getUsersPending: (state: UsersStateT) => {
            state.status = undefined
        },
        getUsersFulfilled: (state: UsersStateT, action: PayloadAction<ListT<UserT>>) => {
            state.status = Status.OK
            state.data = action.payload.data.map(getCreatedAt)
            state.total = action.payload.total
        },
        getVerifyUsersFulfilled: (state: UsersStateT, action: PayloadAction<UserT>) => {
            state.status = Status.OK
            state.data = state.data.map((x) => {
                if (x._id === action.payload._id) return action.payload
                return x
            })
        },
        getUsersRejected: (state: UsersStateT, action: PayloadAction<string>) => {
            state.status = action.payload
        },
    },
})

export const { getUsersFulfilled, getVerifyUsersFulfilled, getUsersPending, getUsersRejected } =
    usersSlice.actions

export const getUsersAsync =
    (queryOptions: QueryOptionsT): AppThunkT =>
    async (dispatch) => {
        try {
            dispatch(getUsersPending())
            const users = await get<ListT<UserT>>("users", queryOptions)
            dispatch(getUsersFulfilled(users))
        } catch (error: any) {
            dispatch(getUsersRejected(error.message))
        }
    }

export const checkUsersAsync =
    (email: string): AppThunkT =>
    async (dispatch) => {
        try {
            dispatch(getUsersPending())
            const users = await get<ListT<UserT>>("users/check", { email })
            dispatch(getUsersFulfilled(users))
        } catch (error: any) {
            dispatch(getUsersRejected(error.message))
        }
    }

export const verifyUsersAsync =
    (id: string, isVerified: boolean): AppThunkT =>
    async (dispatch) => {
        try {
            dispatch(getUsersPending())
            await put("companies/verification", { isVerified, userId: id })
            const user = await get<UserT>(`users/${id}`)
            dispatch(getVerifyUsersFulfilled(user))
            const { success } = useToast()
            success("user:successMsg.verify")
        } catch (error: any) {
            dispatch(getUsersRejected(error.message))
        }
    }

export default usersSlice.reducer
